'use client';

import React, { useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import type { ReactImageGalleryItem } from 'react-image-gallery';
import { Image } from '../../../types';

export type ImagesRendererImage = Image | ReactImageGalleryItem;

export type ImagesRendererProps = {
    images: ImagesRendererImage[];
    BUSINESS_NAME?: string;
    showLightbox?: boolean;
    showOpenIcon?: boolean;
    showCaptions?: boolean;
    className?: string;
    imageClassName?: string;
    openLabel?: string;
    showGallery?: boolean;
    showSlider?: boolean;
    sliderProps?: Record<string, unknown>;
};

const imagesRendererCss = `
.images-renderer {
  display: grid;
  width: 100%;
  gap: 16px;
  margin-top: 4px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.images-renderer--single {
  grid-template-columns: 1fr;
}

.images-renderer-figure {
  width: 100%;
  min-width: 0;
  margin: 0;
}

.images-renderer-button,
.images-renderer-static {
  display: block;
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(17, 24, 39, 0.1);
  border-radius: 8px;
  background: #ffffff;
  color: inherit;
  padding: 0;
}

.images-renderer-button {
  cursor: zoom-in;
  font: inherit;
}

.images-renderer-button:focus-visible {
  outline: 3px solid rgba(184, 0, 0, 0.32);
  outline-offset: 3px;
}

.images-renderer-img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: filter 180ms ease, transform 180ms ease;
}

.images-renderer-button:hover .images-renderer-img,
.images-renderer-button:focus-visible .images-renderer-img {
  filter: saturate(1.04);
}

.images-renderer-open-icon {
  display: inline-flex;
  position: absolute;
  z-index: 2;
  top: 12px;
  right: 12px;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.72);
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
  transition: background-color 160ms ease, transform 160ms ease;
}

.images-renderer-button:hover .images-renderer-open-icon,
.images-renderer-button:focus-visible .images-renderer-open-icon {
  background: #b80000;
  transform: translateY(-1px);
}

.images-renderer-caption {
  margin: 8px 0 0;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.45;
  font-weight: 650;
}

.images-renderer-lightbox {
  --yarl__slide_captions_container_background: rgba(0, 0, 0, 0.72);
  --yarl__slide_captions_container_padding: 14px 18px;
  --yarl__slide_description_color: rgba(255, 255, 255, 0.94);
  --yarl__slide_description_text_align: center;
}

.images-renderer-lightbox .yarl__slide_captions_container {
  font-family: inherit;
}

.images-renderer-lightbox .yarl__slide_description {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.45;
}

@media (max-width: 640px) {
  .images-renderer {
    grid-template-columns: 1fr;
  }
}
`;

const getImageSrc = (image: ImagesRendererImage) => {
    const value = image as Image & ReactImageGalleryItem & { src?: string };
    return value.url || value.src || value.original || value.thumbnail || '';
};

const getImageTitle = (image: ImagesRendererImage, fallback?: string) => {
    const value = image as Image & ReactImageGalleryItem;
    return value.title || value.originalTitle || value.thumbnailTitle || fallback || '';
};

const getImageAlt = (image: ImagesRendererImage, fallback?: string) => {
    const value = image as Image & ReactImageGalleryItem;
    return value.alt || value.originalAlt || value.thumbnailAlt || getImageTitle(image, fallback) || fallback || '';
};

const getImageCaption = (image: ImagesRendererImage, fallback?: string) => {
    const value = image as Image & ReactImageGalleryItem;
    return value.caption || value.description || getImageTitle(image, fallback);
};

const getImageKey = (image: ImagesRendererImage, index: number) => {
    const value = image as Image & ReactImageGalleryItem;
    return String(value.id || getImageSrc(image) || `${getImageTitle(image, 'image')}-${index}`);
};

const ImagesRenderer = ({
    images,
    BUSINESS_NAME,
    showLightbox = true,
    showOpenIcon = true,
    showCaptions = false,
    className,
    imageClassName,
    openLabel = 'Open image in full screen',
}: ImagesRendererProps) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const normalisedImages = useMemo(() => (images || []).filter((image) => !!getImageSrc(image)), [images]);
    const slides = normalisedImages.map((image) => {
        const caption = getImageCaption(image, BUSINESS_NAME);

        return {
            src: getImageSrc(image),
            alt: getImageAlt(image, BUSINESS_NAME),
            description: caption || undefined,
        };
    });

    if (!normalisedImages.length) return null;

    const rootClassName = [
        'images-renderer',
        normalisedImages.length === 1 ? 'images-renderer--single' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            <style>{imagesRendererCss}</style>
            <div className={rootClassName}>
                {normalisedImages.map((image, index) => {
                    const src = getImageSrc(image);
                    const title = getImageTitle(image, BUSINESS_NAME);
                    const alt = getImageAlt(image, BUSINESS_NAME);
                    const caption = getImageCaption(image, BUSINESS_NAME);
                    const imageElement = (
                        <img
                            src={src}
                            title={title || undefined}
                            alt={alt}
                            loading="lazy"
                            decoding="async"
                            className={['images-renderer-img', imageClassName].filter(Boolean).join(' ')}
                        />
                    );

                    return (
                        <figure className="images-renderer-figure" key={getImageKey(image, index)}>
                            {showLightbox ? (
                                <button
                                    type="button"
                                    className="images-renderer-button"
                                    aria-label={`${openLabel}: ${alt || title || `image ${index + 1}`}`}
                                    title={openLabel}
                                    onClick={() => setActiveIndex(index)}
                                >
                                    {imageElement}
                                    {showOpenIcon ? (
                                        <span className="images-renderer-open-icon" aria-hidden="true">
                                            <Maximize2 size={18} strokeWidth={2.4} />
                                        </span>
                                    ) : null}
                                </button>
                            ) : (
                                <span className="images-renderer-static">{imageElement}</span>
                            )}
                            {showCaptions && caption ? <figcaption className="images-renderer-caption">{caption}</figcaption> : null}
                        </figure>
                    );
                })}
            </div>
            {showLightbox ? (
                <Lightbox
                    slides={slides}
                    open={activeIndex >= 0 && activeIndex < slides.length}
                    index={activeIndex}
                    close={() => setActiveIndex(-1)}
                    plugins={[Captions, Fullscreen, Zoom]}
                    captions={{ showToggle: false, descriptionTextAlign: 'center', descriptionMaxLines: 4 }}
                    carousel={{ finite: slides.length <= 1 }}
                    controller={{ aria: true }}
                    className="images-renderer-lightbox"
                    labels={{ Slide: 'Gallery image', Caption: 'Image caption' }}
                />
            ) : null}
        </>
    );
};

export default ImagesRenderer;
